import { useState, useEffect } from 'react';
import { GripVertical, ChevronDown, ChevronUp, Save, X, Package, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductOrderManager({ brands, products, getAuthHeader, onUpdate }) {
  const [localBrands, setLocalBrands] = useState([]);
  const [productsByBrand, setProductsByBrand] = useState({});
  const [expandedBrands, setExpandedBrands] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Sort brands by display_order
    const sorted = [...brands].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setLocalBrands(sorted);
    
    // Group products by brand and sort by display_order
    const grouped = {};
    products.forEach(p => {
      const brandName = p.brand || 'No Brand';
      if (!grouped[brandName]) grouped[brandName] = [];
      grouped[brandName].push(p);
    });
    
    // Sort products within each brand
    Object.keys(grouped).forEach(brand => {
      grouped[brand].sort((a, b) => (a.display_order || 9999) - (b.display_order || 9999));
    });
    
    setProductsByBrand(grouped);
  }, [brands, products]);

  const toggleBrand = (brandName) => {
    setExpandedBrands(prev => ({ ...prev, [brandName]: !prev[brandName] }));
  };

  // Move brand up/down
  const moveBrand = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= localBrands.length) return;
    
    const newBrands = [...localBrands];
    [newBrands[index], newBrands[newIndex]] = [newBrands[newIndex], newBrands[index]];
    
    setLocalBrands(newBrands);
    setIsDirty(true);
  };

  // Move product up/down within brand
  const moveProduct = (brandName, index, direction) => {
    const brandProducts = productsByBrand[brandName] || [];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= brandProducts.length) return;
    
    const newProducts = [...brandProducts];
    [newProducts[index], newProducts[newIndex]] = [newProducts[newIndex], newProducts[index]];
    
    setProductsByBrand(prev => ({
      ...prev,
      [brandName]: newProducts
    }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save brand order
      const brandIds = localBrands.map(b => b.id);
      if (brandIds.length > 0) {
        await axios.put(`${API}/admin/brands/reorder`, { brand_ids: brandIds }, { headers: getAuthHeader() });
      }
      
      // Save product order for each brand (skip brands with no products)
      for (const brandName of Object.keys(productsByBrand)) {
        const brandProducts = productsByBrand[brandName];
        if (brandProducts && brandProducts.length > 0) {
          const productIds = brandProducts.map(p => p.id).filter(id => id); // Filter out undefined/null
          if (productIds.length > 0) {
            await axios.put(`${API}/admin/products/reorder`, { 
              product_ids: productIds,
              brand: brandName !== 'No Brand' ? brandName : null
            }, { headers: getAuthHeader() });
          }
        }
      }
      
      // Clear catalog cache so changes appear immediately
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('catalog_')) {
          sessionStorage.removeItem(key);
        }
      });
      
      toast.success('PRODUCT ORDER SAVED');
      setIsDirty(false);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save order: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
      // Refresh data after save (regardless of success/failure)
      if (onUpdate) {
        try {
          onUpdate();
        } catch (e) {
          // Ignore refresh errors
        }
      }
    }
  };

  const handleReset = () => {
    // Re-sort from original data
    const sorted = [...brands].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    setLocalBrands(sorted);
    
    const grouped = {};
    products.forEach(p => {
      const brandName = p.brand || 'No Brand';
      if (!grouped[brandName]) grouped[brandName] = [];
      grouped[brandName].push(p);
    });
    Object.keys(grouped).forEach(brand => {
      grouped[brand].sort((a, b) => (a.display_order || 9999) - (b.display_order || 9999));
    });
    setProductsByBrand(grouped);
    setIsDirty(false);
  };

  return (
    <div className="space-y-4">
      {/* Header with Save/Reset */}
      <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
        <p className="font-mono text-xs text-zinc-500">
          USE ARROWS TO REORDER BRANDS & PRODUCTS
        </p>
        <div className="flex gap-2">
          {isDirty && (
            <>
              <Button
                size="sm"
                onClick={handleReset}
                className="h-7 px-3 bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded-none font-mono text-xs"
              >
                <X className="w-3 h-3 mr-1" />
                RESET
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-7 px-3 bg-tiffany-500 text-black hover:bg-tiffany-400 rounded-none font-mono text-xs"
              >
                <Save className="w-3 h-3 mr-1" />
                {saving ? 'SAVING...' : 'SAVE ORDER'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Brand List with Products */}
      <div className="space-y-1">
        {localBrands.map((brand, brandIndex) => {
          const brandProducts = productsByBrand[brand.name] || [];
          const isExpanded = expandedBrands[brand.name];
          
          return (
            <div key={brand.id} className="border border-zinc-800">
              {/* Brand Header */}
              <div className="flex items-center gap-2 p-2 bg-zinc-900/50">
                {/* Up/Down Arrows for Brand */}
                <div className="flex flex-col">
                  <button
                    onClick={() => moveBrand(brandIndex, 'up')}
                    disabled={brandIndex === 0}
                    className={`p-0.5 ${brandIndex === 0 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                    title="Move up"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveBrand(brandIndex, 'down')}
                    disabled={brandIndex === localBrands.length - 1}
                    className={`p-0.5 ${brandIndex === localBrands.length - 1 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                    title="Move down"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                
                <span 
                  className="w-4 h-4 flex-shrink-0 rounded-sm" 
                  style={{ backgroundColor: brand.color }}
                />
                <span className="font-mono text-sm text-white flex-1 uppercase">
                  {brand.name.toUpperCase()}
                </span>
                <span className="font-mono text-xs text-zinc-500">
                  {brandProducts.length} ITEMS
                </span>
                <button
                  onClick={() => toggleBrand(brand.name)}
                  className="p-1 hover:bg-zinc-800 rounded"
                >
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-zinc-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-zinc-500" />
                  )}
                </button>
              </div>
              
              {/* Products in Brand - Collapsible */}
              {isExpanded && brandProducts.length > 0 && (
                <div className="bg-zinc-950/50 border-t border-zinc-800">
                  {brandProducts.map((product, productIndex) => (
                    <div
                      key={product.id}
                      className="flex items-center gap-2 p-2 pl-4 border-b border-zinc-800/50 last:border-b-0 hover:bg-zinc-900/30 transition-colors"
                    >
                      {/* Up/Down Arrows for Product */}
                      <div className="flex flex-col">
                        <button
                          onClick={() => moveProduct(brand.name, productIndex, 'up')}
                          disabled={productIndex === 0}
                          className={`p-0.5 ${productIndex === 0 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                          title="Move up"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveProduct(brand.name, productIndex, 'down')}
                          disabled={productIndex === brandProducts.length - 1}
                          className={`p-0.5 ${productIndex === brandProducts.length - 1 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                          title="Move down"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt="" 
                          className="w-8 h-8 object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <Package className="w-4 h-4 text-zinc-600" />
                        </div>
                      )}
                      <span className="font-mono text-xs text-zinc-300 flex-1 truncate uppercase">
                        {product.name}
                      </span>
                      <span className="font-mono text-xs text-tiffany-500">
                        ${product.price?.toFixed(2)}
                      </span>
                      {(product.visibility === 'offline' || (!product.visibility && product.is_online === false)) && (
                        <span className="font-mono text-[10px] text-red-500 bg-red-500/10 px-1">
                          OFFLINE
                        </span>
                      )}
                      {product.visibility === 'coming_soon' && (
                        <span className="font-mono text-[10px] text-yellow-500 bg-yellow-500/10 px-1">
                          SOON
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Empty state */}
              {isExpanded && brandProducts.length === 0 && (
                <div className="p-4 text-center">
                  <p className="font-mono text-xs text-zinc-600">NO PRODUCTS</p>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Products without brand */}
        {productsByBrand['No Brand']?.length > 0 && (
          <div className="border border-zinc-800 mt-4">
            <div className="flex items-center gap-2 p-2 bg-zinc-900/50">
              <div className="w-6" /> {/* Spacer for alignment */}
              <span className="w-4 h-4 flex-shrink-0 rounded-sm bg-zinc-600" />
              <span className="font-mono text-sm text-zinc-400 flex-1">
                NO BRAND
              </span>
              <span className="font-mono text-xs text-zinc-500">
                {productsByBrand['No Brand'].length} ITEMS
              </span>
              <button
                onClick={() => toggleBrand('No Brand')}
                className="p-1 hover:bg-zinc-800 rounded"
              >
                {expandedBrands['No Brand'] ? (
                  <ChevronUp className="w-4 h-4 text-zinc-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-zinc-500" />
                )}
              </button>
            </div>
            
            {expandedBrands['No Brand'] && (
              <div className="bg-zinc-950/50 border-t border-zinc-800">
                {productsByBrand['No Brand'].map((product, productIndex) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-2 p-2 pl-4 border-b border-zinc-800/50 last:border-b-0 hover:bg-zinc-900/30 transition-colors"
                  >
                    {/* Up/Down Arrows for Product */}
                    <div className="flex flex-col">
                      <button
                        onClick={() => moveProduct('No Brand', productIndex, 'up')}
                        disabled={productIndex === 0}
                        className={`p-0.5 ${productIndex === 0 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                        title="Move up"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveProduct('No Brand', productIndex, 'down')}
                        disabled={productIndex === productsByBrand['No Brand'].length - 1}
                        className={`p-0.5 ${productIndex === productsByBrand['No Brand'].length - 1 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-500 hover:text-tiffany-500'}`}
                        title="Move down"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt="" 
                        className="w-8 h-8 object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center flex-shrink-0">
                        <Package className="w-4 h-4 text-zinc-600" />
                      </div>
                    )}
                    <span className="font-mono text-xs text-zinc-300 flex-1 truncate uppercase">
                      {product.name}
                    </span>
                    <span className="font-mono text-xs text-tiffany-500">
                      ${product.price?.toFixed(2)}
                    </span>
                    {(product.visibility === 'offline' || (!product.visibility && product.is_online === false)) && (
                      <span className="font-mono text-[10px] text-red-500 bg-red-500/10 px-1">
                        OFFLINE
                      </span>
                    )}
                    {product.visibility === 'coming_soon' && (
                      <span className="font-mono text-[10px] text-yellow-500 bg-yellow-500/10 px-1">
                        SOON
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
